import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area,
} from 'recharts';
import { apiClient } from '../services/http.js';

const CHART_COLOR = '#f2ca50';

export default function InsightsPage() {
  const [insights, setInsights] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    apiClient.request(`/auth/me/insights?days=${days}`).then(({ res, data }) => {
      if (res.ok) setInsights(data);
    });
  }, [days]);

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <Link to="/chat" className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
            <span>←</span> Voltar para o Chat
          </Link>
          <h1 className="text-2xl font-bold">Painel de Insights Espirituais</h1>
        </div>

        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                days === d ? 'bg-primary text-on-primary border-primary' : 'border-outline-variant text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {d} dias
            </button>
          ))}
        </div>

        {insights === null ? (
          <p className="text-sm text-on-surface-variant">Carregando...</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
                <p className="text-xs text-on-surface-variant uppercase tracking-wider">Mensagens no período</p>
                <p className="text-3xl font-bold text-primary mt-1">{insights.total_messages}</p>
              </div>
              <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
                <p className="text-xs text-on-surface-variant uppercase tracking-wider">Tema mais estudado</p>
                <p className="text-xl font-bold mt-1">
                  {insights.by_intent.find((b) => b.intent === insights.most_studied_intent)?.label || '—'}
                </p>
              </div>
            </div>

            <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
              <h2 className="font-bold mb-4">Constância de Engajamento</h2>
              {insights.daily_activity.length === 0 ? (
                <p className="text-sm text-on-surface-variant">Sem dados suficientes neste período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={insights.daily_activity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-outline-variant)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      stroke="var(--color-on-surface-variant)"
                      fontSize={12}
                    />
                    <YAxis allowDecimals={false} stroke="var(--color-on-surface-variant)" fontSize={12} />
                    <Tooltip
                      labelFormatter={(d) => new Date(d).toLocaleDateString('pt-BR')}
                      contentStyle={{ backgroundColor: 'var(--color-surface-container-high)', border: 'none', borderRadius: 8 }}
                    />
                    <Area type="monotone" dataKey="count" name="Mensagens" stroke={CHART_COLOR} fill={CHART_COLOR} fillOpacity={0.25} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4">
              <h2 className="font-bold mb-4">Temas Estudados</h2>
              {insights.by_intent.length === 0 ? (
                <p className="text-sm text-on-surface-variant">Sem dados suficientes neste período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(180, insights.by_intent.length * 45)}>
                  <BarChart data={insights.by_intent} layout="vertical" margin={{ left: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-outline-variant)" />
                    <XAxis type="number" allowDecimals={false} stroke="var(--color-on-surface-variant)" fontSize={12} />
                    <YAxis type="category" dataKey="label" stroke="var(--color-on-surface-variant)" fontSize={12} width={120} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--color-surface-container-high)', border: 'none', borderRadius: 8 }} />
                    <Bar dataKey="count" name="Mensagens" fill={CHART_COLOR} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
